import { useEffect, useMemo, useState } from 'react'
import { appStyles } from './app.style'
import { useEspecialidades } from '../data/hooks/useEspecialidades.hook'
import { useOpcionesTurnos } from '../data/hooks/useMedicos.hook'
import { useTriage } from '../data/hooks/useTriage.hook'
import { Especialidad } from '../data/types/especialidades.types'
import { formatStringToUserFullDate } from '../helpers/dates.helper'
import { api } from '../data/api'
import { ContainerApp, ContentApp, FooterApp, HeaderApp, LabelApp } from './Layout.app'
import { Turno, TurnoStatus } from '../data/types/turnos.type'
import { TurnoCard } from './TurnoCard.app'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Spinner } from '../components/spinner/Spinner.component'
import { ButtonInApp } from './ButtonInApp.component'
import { useTurnoDelPaciente } from '../data/hooks/useTurnos.hook'

const APP_URL = process.env.REACT_APP_APP_URL;
interface WizData {
	triageId?: number
	especialidadId?: number
	medicoId?: number
	fecha?: string
	hora?: string
	motivoConsulta?: string
	terminos?: boolean
	statusTurno?: TurnoStatus
}

enum WizardStep {
	TRIAGE = 'TRIAGE',
	ESPECIALIDAD = 'ESPECIALIDAD',
	MEDICO = 'MEDICO',
	FECHA = 'FECHA',
	HORA = 'HORA',
	MOTIVO_CONSULTA = 'MOTIVO_CONSULTA',
	TERMINOS = 'TERMINOS',
	STATUS_PAGO = 'STATUS_PAGO',
}

interface SimpleSelectorItem {
	label: string
	value: string
}

const SimpleSelector = (
	list: SimpleSelectorItem[], 
	setData: (itemData: SimpleSelectorItem) => void
) => {

	return (
		<>
			{list.map((item, index) => (				
				<button 
					key={index}
					type="submit" 
					style={{...appStyles.button, marginBottom: '20px', backgroundColor: '#1CAEBD'}}
					onClick={() => setData(item)}
				>
					{item.label}
				</button>
			))}
		</>
	)
}

interface MedicoSelectorItem {
	labelNombreCompleto: string
	labelEspecialidad: string
	labelMatricula: string
	labelTarifa: number
	value: string
}

const MedicoSelector = (list: MedicoSelectorItem[], setData: (itemData: MedicoSelectorItem) => void) => {
	return (
		<>
			{list.map((item, index) => (				
				<button 
					key={index}
					type="submit" 
					style={{...appStyles.button, marginBottom: '20px', backgroundColor: '#1CAEBD'}}
					onClick={() => setData(item)}
				>
					{item.labelNombreCompleto}
					<br />
					{`${item.labelEspecialidad} (MN ${item.labelMatricula}) - $${item.labelTarifa}`}
				</button>
			))}
		</>
	)
}

const ResumenSelector = ({
	especialidadLabel,
	medicoLabel,
	fechaLabel,
	horaLabel,
}: {
	especialidadLabel: string,
	medicoLabel: string,
	fechaLabel: string,
	horaLabel: string,
}) => {
	return (
		<>
			<h3>Resumen</h3>
			<p>{medicoLabel}</p>
			<p>({especialidadLabel})</p>
			<p>{formatStringToUserFullDate(fechaLabel)}</p>
			<p>{horaLabel} horas</p>
		</>
	)
}

const MotivoConsultaSelector = ({
	wizData,
	setWizData,
	handleConfirmar
}: {
	wizData: WizData,
	setWizData: Function,
	handleConfirmar: Function
}) => {

	const [motivoConsulta, setMotivoConsulta] = useState<string>('')

	return (
		<>
			<LabelApp label="Motivo de la consulta" />
			<textarea 
				placeholder="Ingrese el motivo de la consulta" 
				style={appStyles.textarea}
				onChange={(e) => setMotivoConsulta(e.target.value)}
				value={motivoConsulta}
			/>
			<input 
				type="checkbox"
				onChange={(e) => setWizData({ ...wizData, terminos: e.target.checked })}
				checked={wizData.terminos}
			/>
			<label>
				Acepto los 
				<span 
					style={{
						color: '#1CAEBD', 
						textDecoration: 'underline', 
						cursor: 'pointer'
					}} 
					onClick={() => window.open(`${APP_URL}/app/turno-terms`, '_blank')}
				>
					{' términos y condiciones'}
				</span>
			</label>
			<button 
				type="button" 
				style={{
					...appStyles.button, 
					marginBottom: '20px', 
					marginTop: '20px', 
					...(wizData.terminos &&{backgroundColor: '#1CAEBD'}),
					...(!wizData.terminos && {
						background: '#cbd5e0',
						borderColor: '#cbd5e0',
						cursor: 'not-allowed',
						color: '#718096'
					})

				}}
				onClick={() => {
					setWizData({ ...wizData, motivoConsulta })
					handleConfirmar(motivoConsulta)
				}}
				disabled={!wizData.terminos}
			>
				Confirmar
			</button>
		</>
	)
}

const NoMedicosDisponibles = () => {
	return (
		<h3>No hay médicos disponibles</h3>
	)
}

export const WizardApp = () => {
	const [especialidadesFiltradas, setEspecialidadesFiltradas] = useState<Especialidad[]>([])
	const [wizData, setWizData] = useState<WizData>({})
	// REVISAR "turnoCompleto" PORQUE aparentemente no se usa...
	const [turnoCompleto, setTurnoCompleto] = useState<Turno | null>(null)
	const [fijados, setFijados] = useState<WizData>({})
	const [saving, setSaving] = useState(false)

	const [searchParams] = useSearchParams()
	const navigate = useNavigate()

	const noBack = (searchParams.get('noBack') === 'true')
	const turnoIdParam = searchParams.get('turnoId')
	const turnoId = turnoIdParam ? Number(turnoIdParam) : undefined
	const actionType = searchParams.get('actionType')

	const { 
		data: dataEspecialidades, 
		isPending: especialidadPending, 
		isError: especialidadIsError, error: especialidadError 
	} = useEspecialidades();

	const { 
		data: dataTriage, 
		isPending: triagePending, 
		isError: triageIsError, 
		error: triageError 
	} = useTriage(true);

	const { 
		data: dataOpcionesTurnos, 
		isPending: opcionesTurnosPending, 
		isError: opcionesTurnosIsError, 
		error: opcionesTurnosError 
	} = useOpcionesTurnos(wizData.especialidadId);

	const { 
		data: dataTurno, 
		isPending: turnoPending, 
		isError: turnoIsError, 
		error: turnoError 
	} = useTurnoDelPaciente(turnoId)

	useEffect(() => {
		if (dataTurno) {
			if (actionType === 'programar') {
				setWizData((prev) => ({
					...prev,
					triageId: 999,
					especialidadId: dataTurno.especialidadId,
					medicoId: dataTurno.medicoDni,
				}))
			} else if (actionType === 'reprogramar') {
				setWizData((prev) => ({
					...prev,
					triageId: 999,
					especialidadId: dataTurno.especialidadId,
					medicoId: dataTurno.medicoDni,
					statusTurno: TurnoStatus.PENDIENTE,
				}))
			}
		}
	}, [dataTurno, actionType])

	useEffect(() => {
		if (dataTriage?.length === 1) {
			setWizData((prev) => ({ ...prev, triageId: dataTriage[0].id }))
			setFijados((prev) => ({ ...prev, triageId: dataTriage[0].id }))
		}
		
	}, [dataTriage])

	const wizardSelector = useMemo(() => {
		if (!wizData.triageId) return WizardStep.TRIAGE
		if (!wizData.especialidadId) return WizardStep.ESPECIALIDAD
		if (!wizData.medicoId) return WizardStep.MEDICO
		if (!wizData.fecha) return WizardStep.FECHA
		if (!wizData.hora) return WizardStep.HORA
		if (!wizData.motivoConsulta) return WizardStep.MOTIVO_CONSULTA
		if (!wizData.terminos) return WizardStep.TERMINOS

		return WizardStep.STATUS_PAGO // Paso final cuando todo está completo
	}, [wizData])

	const getDisponFechas = useMemo(() => {
		const medicoData =  dataOpcionesTurnos?.find((dot) => dot.dni === wizData.medicoId)
		return medicoData?.disponibilidades || []
	}, [dataOpcionesTurnos, wizData.medicoId])

	const getDisponHoras = useMemo(() => {
		const disponFechas = getDisponFechas?.find((df) => df.fecha === wizData.fecha)?.horarios || []
		return disponFechas || []
	}, [dataOpcionesTurnos, wizData.medicoId, wizData.fecha])

	useEffect(() => {
		const selectedTriage = dataTriage?.find((triage) => Number(triage.id) === Number(wizData.triageId))
		setEspecialidadesFiltradas(dataEspecialidades?.filter((especialidad) => 
			selectedTriage?.especialidades.includes(especialidad.id.toString()))
			|| []
		)
	}, [wizData.triageId])

	const handleBack = () => {
		if (turnoCompleto  || (wizardSelector === WizardStep.TRIAGE && !noBack)) {
			navigate('/app/menu')
		} else if (wizardSelector === WizardStep.ESPECIALIDAD) {
			setEspecialidadesFiltradas([])
			setWizData({ ...wizData, triageId: undefined })
		} else if (wizardSelector === WizardStep.MEDICO) {
			setWizData({ ...wizData, especialidadId: undefined })
		} else if (wizardSelector === WizardStep.FECHA) {
			setWizData({ ...wizData, medicoId: undefined })
		} else if (wizardSelector === WizardStep.HORA) {
			setWizData({ ...wizData, fecha: undefined })
		} else if (wizardSelector === WizardStep.MOTIVO_CONSULTA) {
			setWizData({ ...wizData, hora: undefined })
		}
	}

	const handleTriageSelection = (item: SimpleSelectorItem) => {
		setWizData({ ...wizData, triageId: Number(item.value) })
	}

	const handleEspecialidadSelection = (item: SimpleSelectorItem) => {
		setWizData({ ...wizData, especialidadId: Number(item.value) })
	}

	const handleMedicoSelection = (item: MedicoSelectorItem) => {
		setWizData({ ...wizData, medicoId: Number(item.value) })
	}

	const handleFechaSelection = (item: SimpleSelectorItem) => {
		setWizData({ ...wizData, fecha: item.value })
	}

	const handleHoraSelection = (item: SimpleSelectorItem) => {
		setWizData({ ...wizData, hora: item.value })
	}

	const handleConfirmar = async (motivoConsulta: string) => {
		setSaving(true)
		setWizData({ ...wizData, terminos: true })

		try {

			const turnoToSave = {
				especialidadId: wizData.especialidadId,
				medicoDni: wizData.medicoId,
				fecha: wizData.fecha,
				hora: wizData.hora,
				motivoConsulta: motivoConsulta || '',
				statusTurno: wizData.statusTurno || TurnoStatus.IMPAGO,
			}
			
			const turnoSaved = await (await api.post('/api/turnos/new', turnoToSave)).data
			
			if (turnoToSave.statusTurno === TurnoStatus.IMPAGO) {
				const paymentResponse = await api.get(`/api/payments/generatePayment/${turnoSaved.id}`)
				window.open(paymentResponse.data.paymentUrl, '_blank')
				if (actionType === 'first') {
					navigate('/app/mi-perfil')
				} else {
					navigate('/app/turnos-pendientes')
				}
			}
		} catch (error) {
			console.error("### error", error)
		} finally {
			setSaving(false)
		}
	}

	if (
		especialidadPending || 
		triagePending || 
		(wizData.especialidadId && opcionesTurnosPending) || 
		saving
	) 
		return <Spinner />

	return (
		<ContainerApp>
			<HeaderApp title="Reserva" />
			<ContentApp>
				{wizardSelector === WizardStep.TRIAGE && 
					SimpleSelector(
						dataTriage?.map((item) => ({ 
							label: item.nombre, 
							value: item.id.toString() 
						} as SimpleSelectorItem)) || [],
						handleTriageSelection
					)
				}
				{wizardSelector === WizardStep.ESPECIALIDAD && 
					SimpleSelector(
						especialidadesFiltradas.map((item) => ({ 
							label: item.nombre, 
							value: item.id.toString() 
						} as SimpleSelectorItem)) || [],
						handleEspecialidadSelection
					)
				}
				{wizardSelector === WizardStep.MEDICO && !dataOpcionesTurnos?.length && (
					<NoMedicosDisponibles />
				)}
				{wizardSelector === WizardStep.MEDICO && dataOpcionesTurnos && dataOpcionesTurnos.length > 0 && (
					MedicoSelector(
						dataOpcionesTurnos?.map((item) => ({ 
							labelNombreCompleto: `${item.nombre} ${item.apellido}`, 
							labelEspecialidad: wizData.especialidadId ? especialidadesFiltradas.find((especialidad) => especialidad.id === wizData.especialidadId)?.nombre : '', 
							labelMatricula: item.matricula,
							labelTarifa: item.tarifa,
							value: item.dni.toString() 
						} as MedicoSelectorItem)) || [],
						handleMedicoSelection
					)
				)}
				{wizardSelector === WizardStep.FECHA && 
					SimpleSelector(
						getDisponFechas?.map((item) => ({ 
							label: formatStringToUserFullDate(item.fecha),
							value: item.fecha, 
						} as SimpleSelectorItem)) || [],
						handleFechaSelection
					)
				}
				{wizardSelector === WizardStep.HORA && 
					SimpleSelector(
						getDisponHoras?.map((hora: string) => ({ 
							label: hora, 
							value: hora 
						} as SimpleSelectorItem)) || [],
						handleHoraSelection
					)
				}
				{wizardSelector === WizardStep.MOTIVO_CONSULTA && 
					<>
						<ResumenSelector 
							especialidadLabel={wizData.especialidadId ? especialidadesFiltradas.find((especialidad) => especialidad.id === wizData.especialidadId)?.nombre || '': ''}
							medicoLabel={wizData.medicoId ? dataOpcionesTurnos?.find((dot) => dot.dni === wizData.medicoId)?.nombreCompleto || '' : ''}
							fechaLabel={wizData.fecha ? wizData.fecha : ''}
							horaLabel={wizData.hora ? wizData.hora : ''}
						/>
						<MotivoConsultaSelector 
							wizData={wizData}
							setWizData={setWizData}
							handleConfirmar={handleConfirmar}
						/>
					</>
				}
				{(wizardSelector === WizardStep.TERMINOS && turnoCompleto) && 
					<>
						<TurnoCard turno={turnoCompleto} />
					</>
				}
			</ContentApp>

			<FooterApp>
				<ButtonInApp 
					label="Atrás"
					type="button"
					onClick={handleBack}
					disabled={wizardSelector === WizardStep.TRIAGE && noBack}
				/>
			</FooterApp>
		</ContainerApp>
	)
}